import cn from 'clsx'
import React, { useCallback, useContext, useEffect, useState } from 'react'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageMultiRoomMode, OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { getMultiRoomPackagesForFilters, getSelectedOfferPageMultiRoomDay } from 'selectors/offerPage/multipleRoomBookingSelectors'
import { CALENDAR_GA_EVENT_ACTION } from 'constants/analytics'
import UserAnalyticsActionContext from 'contexts/userAnalyticsActionContext'
import Group from 'components/utils/Group'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import TextButton from 'LuxKit/Button/TextButton'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import OfferCalenderDatePickerFooterPriceDetails from '../CalendarPicker/OfferCalenderDatePickerFooterPriceDetails'
import OfferBookButton from 'components/OfferPage/OfferBookButtons/OfferBookButton'
import { canBookNow, getPackagesForFilters, getSelectedOfferPageDay, getSelectedPackageView } from 'selectors/offerPage/offerPageSelectors'
import LineArrowRightIcon from 'LuxKit/Icons/line/LineArrowRightIcon'
import { INTL_SHORT_DAY_NAME_DAY_MONTH } from 'constants/dateFormats'
import BodyText from 'LuxKit/Typography/BodyText'
import styled from 'styled-components'
import eases from 'styles/tools/eases'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import { OFFER_TYPE_HOTEL, OFFER_TYPE_VILLA } from 'constants/offer'
import OfferAddToCartButton from 'components/OfferPage/OfferBookButtons/OfferAddToCartButton'
import OfferSelectPackageButton from 'components/OfferPage/OfferBookButtons/OfferSelectPackageButton'
import { isMultipleRoomBookingActive } from 'selectors/offerPage/offerPageSharedSelectors'

const FooterContents = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  transform: translateY(100%);
  transition: transform 0.3s ${eases.expo.in};

  &.visible {
    position: unset;
    transform: translateY(0);
    transition: transform 0.3s ${eases.expo.out};
  }
`

const MobileButtonGroup = styled(Group)`
  & > * {
    flex: auto 1 1;
  }
`

interface Props {
  offer: App.Offer
  selectedDay?: App.CalendarDay
  duration: number
  isAnyFlightsActive?: boolean
  relevantPackages: Array<App.OfferPackageOptionView>
  selectedPackageView?: App.OfferPackageOptionView
  bookable?: boolean
  mobileMultiRoomMode: OfferPageMultiRoomMode | undefined
}

function OfferCalendarSidebarDatePickerFooter(props: Props) {
  const {
    offer,
    selectedDay,
    duration,
    isAnyFlightsActive,
    relevantPackages,
    selectedPackageView,
    bookable,
    mobileMultiRoomMode,
  } = props

  // We want to default to continue for any multi room mode purchase to re-confirm the selection
  const canBookNow = !mobileMultiRoomMode && bookable

  // Store the selected day locally and persist it until the footer is hidden
  // otherwise we get janky animation when the day disappears and we have no information
  // to show in the footer
  const [localDay, setLocalDay] = useState<App.CalendarDay | undefined>(selectedDay)
  const onUserAnalyticsAction = useContext(UserAnalyticsActionContext)
  const dispatch = useOfferPageDispatch()
  const intlDateFormatter = useIntlDateFormatter()
  const isMobileScreen = useScreenSizeOnly('mobile')
  const useNewMobileHotelOfferPage = isMobileScreen && offer.type === OFFER_TYPE_HOTEL

  const canAddToUserCart = canBookNow && offer.type !== OFFER_TYPE_VILLA
  const onBookNowButtonClick = useCallback(() => {
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.BookNow)
  }, [onUserAnalyticsAction])

  const onContinue = useCallback(() => {
    dispatch({ type: OfferPageActions.filterContinue })
  }, [dispatch])

  const onResetDates = useCallback(() => {
    dispatch({ type: OfferPageActions.resetDates })
  }, [dispatch])

  useEffect(() => {
    if (selectedDay) {
      setLocalDay(selectedDay)
    }
  }, [selectedDay])

  const onFooterClose = useCallback<React.TransitionEventHandler<HTMLDivElement>>((e) => {
    if (!selectedDay && e.propertyName === 'transition') {
      setLocalDay(undefined)
    }
  }, [selectedDay])

  return <FooterContents
    className={cn({ visible: !!selectedDay })}
    onTransitionEnd={onFooterClose}
  >
    {(!canBookNow || !isAnyFlightsActive) && <ModalFooter>
      {useNewMobileHotelOfferPage && <MobileButtonGroup direction="horizontal" gap={8}>
        <TextButton kind="tertiary" onClick={onResetDates}>
          Reset
        </TextButton>
        {canAddToUserCart && <OfferAddToCartButton
          size="large"
          data-buytype={isAnyFlightsActive ? 'flights' : undefined}
          packageView={selectedPackageView ?? relevantPackages[0]}
        />}
        {canBookNow && <OfferBookButton
          size="large"
          data-buytype={isAnyFlightsActive ? 'flights' : undefined}
          packageView={selectedPackageView ?? relevantPackages[0]}
          onSubmit={onBookNowButtonClick}
        >
          Book now
        </OfferBookButton>}
        {!canBookNow && <TextButton
          kind="primary"
          size="large"
          type="submit"
        >
          Continue
        </TextButton>}
      </MobileButtonGroup>}
      {!useNewMobileHotelOfferPage && <VerticalSpacer gap={8}>
        {localDay && <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
          <BodyText variant="medium">
            <Group direction="horizontal" verticalAlign="center">
              <span>{intlDateFormatter(localDay.checkIn, INTL_SHORT_DAY_NAME_DAY_MONTH)}</span>
              <LineArrowRightIcon size="XS" />
              <span>{intlDateFormatter(localDay.checkOut, INTL_SHORT_DAY_NAME_DAY_MONTH)}</span>
            </Group>
          </BodyText>
          <TextButton kind="tertiary" horizontalOutdent="end" onClick={onResetDates}>
            Reset
          </TextButton>
        </Group>}
        <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="end">
          <div>
            {localDay && <OfferCalenderDatePickerFooterPriceDetails
              offer={offer}
              duration={duration}
              selectedDay={localDay}
              isAnyFlightsActive={isAnyFlightsActive}
              isFromPrice={relevantPackages.length > 1}
            />}
          </div>
          {canAddToUserCart && <>
            {isAnyFlightsActive && <OfferSelectPackageButton
              size="large"
              data-buytype="flights"
              packageView={selectedPackageView ?? relevantPackages[0]}
            />}
            {!isAnyFlightsActive && <OfferAddToCartButton
              size="large"
              packageView={selectedPackageView ?? relevantPackages[0]}
            />}
          </>}
          {canBookNow && <OfferBookButton
            size="large"
            data-buytype={isAnyFlightsActive ? 'flights' : undefined}
            packageView={selectedPackageView ?? relevantPackages[0]}
            onSubmit={onBookNowButtonClick}
          >
            Book now
          </OfferBookButton>}
          {!canBookNow && <TextButton
            kind="primary"
            size="large"
            onClick={onContinue}
          >
            Continue
          </TextButton>}
        </Group>
      </VerticalSpacer>}
    </ModalFooter>}
    {canBookNow && isAnyFlightsActive && <ModalFooter>
      <TextButton kind="primary" size="large" onClick={onContinue}>
        Select package
      </TextButton>
    </ModalFooter>}
  </FooterContents>
}

export default withOfferPageAndReduxState((state, appState) => {
  const isMultiRoom = isMultipleRoomBookingActive(state, appState)
  return {
    offer: state.offer,
    selectedDay: isMultiRoom ? getSelectedOfferPageMultiRoomDay(state, appState) : getSelectedOfferPageDay(appState, state),
    duration: state.duration,
    isAnyFlightsActive: state.flights,
    selectedPackageView: getSelectedPackageView(state, appState),
    relevantPackages: isMultiRoom ? getMultiRoomPackagesForFilters(state, appState) : getPackagesForFilters(appState, state),
    bookable: canBookNow(state, appState),
    mobileMultiRoomMode: state.multiRoomMode,
  }
}, OfferCalendarSidebarDatePickerFooter)
